import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
    BindStatus, getProductMaterialStats, listProductMaterials, MaterialCand,
    ProductMaterialBatchStat, ProductMaterialRow, releaseProductMaterial,
    verdictProductMaterial,
} from '../../api/assets';
import { ApiError } from '../../api/request';
import ImageLightbox from '../../components/ImageLightbox';
import { toast } from '../../utils/toast';
import { useSyncQueryToUrl } from '../../utils/urlState';
import { NoticeBar, SegGroup } from './parts';
import { ProductImg } from './productLabels';
import SearchSelect from '../../components/SearchSelect';

// 商品打标结果关联官方耗材的人工审核 (2026-10-08)。
//
// 审核员在这一页只做一个判断: **这个官方耗材和图上物体的材质是不是等价、
// 激光参数能不能复用**。不是判断「这张图是不是这个耗材的商品图」——
// 被打标的是亚马逊消费品 (手机/笔记本/耳机), 不可能是耗材商品图。
//
// # 形态照搬打标审核页: 逐条大图 + 键盘裁决
//
// 不做表格。两千多条, 表格形态每条都要滚动找位置、点开、回来, 单条成本以秒计;
// 逐条大图 + 快捷键把单条成本压到一次按键。鼠标只是兜底。
//
// # 默认按风险排, 不按 id
//
// 后端默认序是「ΔE>20 或总分<0.75 的排前面」。这两档约占已绑定的四分之一,
// 同样的人工成本下分层抽样的估计精度更高, 而且高风险层审出来的错误直接就是
// 改识别的材料。页面照用后端的序, 不在前端重排。
//
// # 为什么要显示 ΔE 和色块两样
//
// 只给色块, 人眼判不了「差多少算差」(尤其深色之间); 只给 ΔE, 没有直观感受。
// 两个一起看才能快速判断。图必须能点开放大 —— 72px 缩略图看不出插头与外壳的
// 颜色区别, 实测有绑定就是这么错的。

// 一次拉一批暂存前端, 页内靠 ← → 走条目, 走到批尾才发下一次请求。
// 每条一次请求会让远程库的 RTT 变成审核节奏。
const PAGE_SIZE = 50;

// 风险档。'' 在 URL 里要写成 all —— useSyncQueryToUrl 会把空串丢掉, 刷新就回默认值。
const RISK_TABS = [
    { value: 'high', label: '高风险优先' },
    { value: 'all', label: '全部' },
    { value: 'low', label: '仅低风险' },
];

const STATUS_TABS = [
    { value: 'auto', label: '待审' },
    { value: 'confirmed', label: '已确认' },
    { value: 'rejected', label: '已否决' },
    { value: 'review', label: '没绑上' },
    { value: 'all', label: '全部' },
];

// 状态 → 徽标。blocked 不在 tab 里但列表可能出现 (全部档), 所以要有文案。
const STATUS_BADGE: Record<BindStatus, { text: string; cls: string }> = {
    auto: { text: '待审', cls: 'bg-slate-100 text-slate-500' },
    confirmed: { text: '已确认', cls: 'bg-emerald-50 text-emerald-600' },
    rejected: { text: '已否决', cls: 'bg-rose-50 text-rose-600' },
    review: { text: '没绑上', cls: 'bg-amber-50 text-amber-600' },
    blocked: { text: '整类等补齐', cls: 'bg-slate-100 text-slate-400' },
};

// 没绑上的原因码 → 人话。码是 prism 的 gate_reason, 直接摆给审核员等于没说。
const REASON_TEXT: [RegExp, string][] = [
    [/^stage_b_all_colors_far/, '该分类所有官方耗材的颜色都离得太远'],
    [/^stage_d_below_min_accept/, '最高分没过门：颜色与材料名加权后不足 0.7'],
    [/^stage_c_scorer_error/, '材料名比对时出错了（超时或解析失败）'],
    [/覆盖不足|没有已发布材料/, '这一类官方耗材缺颜色数据，整类没参与匹配'],
];
const reasonText = (r: string) => {
    if (!r) return '';
    const hit = REASON_TEXT.find(([re]) => re.test(r));
    return hit ? hit[1] : r;
};

/** 色块 + hex。色块必须带边框: 纯白色块在白底上看不见 */
function Swatch({ hex, size = 14 }: { hex: string; size?: number }) {
    const v = /^#[0-9a-fA-F]{6}$/.test(hex) ? hex : '';
    return (
        <span className="inline-flex items-center gap-1.5">
            <span
                className="inline-block rounded border border-slate-300 shrink-0"
                style={{ width: size, height: size, background: v || 'transparent' }}
                title={v || '无色值'}
            />
            <span className="font-mono text-xs text-slate-500">{v || '—'}</span>
        </span>
    );
}

/** 分数条。总分 0.7 是过门线, 低于 0.75 算擦边 —— 用颜色把这两档标出来 */
function ScoreBar({ score }: { score: number | null }) {
    if (score == null) return <span className="text-xs text-slate-400">—</span>;
    const pct = Math.max(0, Math.min(100, score * 100));
    const cls = score < 0.75 ? 'bg-amber-400' : 'bg-emerald-500';
    return (
        <span className="inline-flex items-center gap-2">
            <span className="inline-block h-1.5 w-16 rounded bg-slate-100 overflow-hidden">
                <span className={`block h-full rounded ${cls}`} style={{ width: `${pct}%` }} />
            </span>
            <span className="font-mono text-xs text-slate-600">{score.toFixed(2)}</span>
        </span>
    );
}

/** ΔE。20 以上标黄 —— 与后端的高风险判据同一个数 */
function DeltaE({ de }: { de: number | null }) {
    if (de == null) return <span className="text-xs text-slate-400">色差未知</span>;
    return (
        <span className={`font-mono text-xs ${de > 20 ? 'text-amber-600 font-semibold' : 'text-slate-500'}`}>
            ΔE {de.toFixed(1)}
        </span>
    );
}

/** 一个耗材候选卡。selected 时高亮, onPick 非空时可点选 */
function CandCard({
    cand, selected, index, onPick, onZoom,
}: {
    cand: MaterialCand; selected?: boolean; index?: number;
    onPick?: () => void; onZoom?: (src: string) => void;
}) {
    return (
        <div
            className={`flex gap-3 rounded-lg border p-2.5 transition ${
                selected ? 'border-sky-400 bg-sky-50/60' : 'border-slate-200 bg-white'
            } ${onPick ? 'cursor-pointer hover:border-sky-300' : ''}`}
            onClick={onPick}
        >
            <ProductImg
                src={cand.image}
                className="w-14 h-14 rounded object-cover bg-slate-100 shrink-0 cursor-zoom-in"
                onClick={(e) => { e.stopPropagation(); if (cand.image) onZoom?.(cand.image); }}
            />
            <div className="min-w-0 flex-1 text-xs leading-relaxed">
                <div className="flex items-center gap-2">
                    {index != null && (
                        <kbd className="px-1.5 py-0.5 rounded bg-slate-100 text-slate-500 font-mono text-[10px]">
                            {index}
                        </kbd>
                    )}
                    <span className="font-medium text-slate-800 truncate">
                        {cand.nameZh || cand.xcode}
                    </span>
                    {cand.gone && (
                        <span className="px-1.5 py-0.5 rounded bg-rose-50 text-rose-600 text-[10px] shrink-0">
                            库里已查不到
                        </span>
                    )}
                </div>
                <div className="mt-1 flex flex-wrap items-center gap-x-3 gap-y-1">
                    <span className="font-mono text-slate-400">{cand.xcode}</span>
                    <Swatch hex={cand.colorHex} />
                    <DeltaE de={cand.deltaE} />
                    <ScoreBar score={cand.totalScore} />
                </div>
            </div>
        </div>
    );
}

/** 详情行 */
function KV({ label, children }: { label: string; children: React.ReactNode }) {
    return (
        <div className="flex items-baseline gap-2 text-xs">
            <span className="text-slate-400 shrink-0" style={{ width: 64 }}>{label}</span>
            <span className="text-slate-700 min-w-0 break-words">{children}</span>
        </div>
    );
}

/** 分段条的一段 */
function Seg({ n, total, color, label }: { n: number; total: number; color: string; label: string }) {
    const w = total ? (n / total) * 100 : 0;
    return <div style={{ width: `${w}%`, background: color }} title={`${label} ${n}`} />;
}

/** 统计视图。数据全部现算，不读任何快照——准确率随审核推进而变，
 *  任何写死的数字第二天就是错的。 */
function StatsView({ stats }: { stats: ProductMaterialBatchStat[] }) {
    const sum = (f: (b: ProductMaterialBatchStat) => number) => stats.reduce((s, b) => s + f(b), 0);
    const total = sum((b) => b.total);
    const bound = sum((b) => b.bound);
    const review = sum((b) => b.review);
    const blocked = sum((b) => b.blocked);
    const audited = sum((b) => b.audited);
    const correct = sum((b) => b.correct);
    const high = sum((b) => b.highRisk);
    const maxTotal = Math.max(...stats.map((b) => b.total), 1);
    const pct = (a: number, b: number) => (b ? Math.round((a * 100) / b) : 0);
    const gaps = stats.filter((b) => b.blocked > 0);
    const C = { bound: '#2a78d6', review: '#eda100', blocked: '#1baf7a' };

    const tile = (n: React.ReactNode, k: string, d: string) => (
        <div className="rounded-xl border border-slate-200 bg-white px-4 py-3">
            <div className="text-2xl font-semibold tabular-nums tracking-tight">{n}</div>
            <div className="text-xs text-slate-400 mt-0.5">{k}</div>
            <div className="text-xs text-slate-600 mt-1">{d}</div>
        </div>
    );

    return (
        <div className="space-y-5">
            <div className="grid gap-3" style={{ gridTemplateColumns: 'repeat(auto-fit,minmax(160px,1fr))' }}>
                {tile(total, '已审核可用商品图', '人已审核且主加工面可见')}
                {tile(bound, '已绑定官方耗材', `占可用池 ${pct(bound, total)}%`)}
                {tile(stats.filter((b) => b.bound > 0).length, '已产出绑定的品类', `共 ${stats.length} 个品类`)}
                {tile(audited ? `${pct(correct, audited)}%` : '—', '人工抽审准确率',
                    audited ? `已审 ${audited} 条，判对 ${correct} 条` : '还没有人工裁决过的行')}
            </div>

            <section>
                <h3 className="text-sm font-semibold">一、{total} 张现在分别在哪</h3>
                <p className="text-xs text-slate-500 mt-1 mb-2 leading-relaxed max-w-3xl">
                    三类里只有前两类进人工。<b>整类跳过的不进人工队列</b>——它等的是工艺侧补
                    color_hex，人工看图也解决不了，放进去只是让人白看一遍。
                </p>
                <div className="flex flex-wrap gap-4 text-xs text-slate-600 mb-2">
                    <span><i className="inline-block w-2.5 h-2.5 rounded-sm mr-1.5" style={{ background: C.bound }} />已绑定 {bound}</span>
                    <span><i className="inline-block w-2.5 h-2.5 rounded-sm mr-1.5" style={{ background: C.review }} />没过门，待人工看 {review}</span>
                    <span><i className="inline-block w-2.5 h-2.5 rounded-sm mr-1.5" style={{ background: C.blocked }} />整类跳过，等工艺侧补齐 {blocked}</span>
                </div>
                <div className="rounded-xl border border-slate-200 bg-white p-4">
                    <div className="flex h-8 rounded-md overflow-hidden gap-0.5">
                        <Seg n={bound} total={total} color={C.bound} label="已绑定" />
                        <Seg n={review} total={total} color={C.review} label="没过门" />
                        <Seg n={blocked} total={total} color={C.blocked} label="整类跳过" />
                    </div>
                    <div className="flex mt-1.5 text-xs text-slate-600">
                        <span style={{ width: `${pct(bound, total)}%` }}>{bound} · {pct(bound, total)}%</span>
                        <span style={{ width: `${pct(review, total)}%` }}>{review} · {pct(review, total)}%</span>
                        <span style={{ width: `${pct(blocked, total)}%` }}>{blocked} · {pct(blocked, total)}%</span>
                    </div>
                </div>
            </section>

            <section>
                <h3 className="text-sm font-semibold">二、按品类看命中率</h3>
                <p className="text-xs text-slate-500 mt-1 mb-2 leading-relaxed max-w-3xl">
                    这是放行要按品类分批的依据：失败模式按品类分布，不是随机分布。
                    ΔE 中位数越大，说明这个品类的绑定越是靠材料名撑过门的。
                </p>
                <div className="rounded-xl border border-slate-200 bg-white p-4">
                    <table className="w-full text-xs">
                        <thead className="text-slate-400">
                            <tr>
                                <th className="text-left font-medium pb-1.5">品类</th>
                                <th className="text-left font-medium pb-1.5 w-2/5">命中</th>
                                <th className="text-right font-medium pb-1.5">绑上/可用</th>
                                <th className="text-right font-medium pb-1.5">ΔE 中位</th>
                                <th className="text-right font-medium pb-1.5">高风险</th>
                            </tr>
                        </thead>
                        <tbody>
                            {stats.map((b) => (
                                <tr key={b.batch} className="border-t border-slate-100">
                                    <td className="py-1.5 text-slate-700">{b.batch}</td>
                                    <td className="py-1.5">
                                        <span className="inline-block w-full h-3.5 rounded bg-slate-100 align-middle">
                                            <span className="block h-3.5 rounded-r" style={{
                                                width: `${(b.bound / maxTotal) * 100}%`,
                                                background: b.bound ? C.bound : '#d8d8d4',
                                                minWidth: b.bound ? undefined : 2,
                                            }} />
                                        </span>
                                    </td>
                                    <td className="py-1.5 text-right tabular-nums text-slate-500">
                                        {b.bound}/{b.total} · {pct(b.bound, b.total)}%
                                    </td>
                                    <td className="py-1.5 text-right tabular-nums text-slate-500">
                                        {b.deMedian == null ? '—' : b.deMedian}
                                    </td>
                                    <td className="py-1.5 text-right tabular-nums text-slate-500">{b.highRisk}</td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            </section>

            {gaps.length > 0 && (
                <section>
                    <h3 className="text-sm font-semibold">三、等工艺侧补齐的品类</h3>
                    <p className="text-xs text-slate-500 mt-1 mb-2 leading-relaxed max-w-3xl">
                        这些品类官方 color_hex 覆盖不足或根本没有对应分类，整类没参与匹配，也不进人工队列。
                    </p>
                    <div className="rounded-xl border border-slate-200 bg-white p-4">
                        <table className="w-full text-xs">
                            <thead className="text-slate-400">
                                <tr><th className="text-left font-medium pb-1.5">品类</th>
                                    <th className="text-right font-medium pb-1.5">卡住</th>
                                    <th className="text-left font-medium pb-1.5 pl-4">说明</th></tr>
                            </thead>
                            <tbody>
                                {gaps.map((b) => (
                                    <tr key={b.batch} className="border-t border-slate-100">
                                        <td className="py-1.5 text-slate-700">{b.batch}</td>
                                        <td className="py-1.5 text-right tabular-nums">{b.blocked}</td>
                                        <td className="py-1.5 pl-4 text-slate-500">{b.blockReason || '—'}</td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                </section>
            )}

            <section>
                <h3 className="text-sm font-semibold">四、人工审核进展</h3>
                <p className="text-xs text-slate-500 mt-1 mb-2 leading-relaxed max-w-3xl">
                    准确率 = 人工确认的就是跑批的判定（没换选）的占比。从备选里换选过的算跑批判错——
                    换选说明跑批选的不是最优，即使最终绑上的耗材是对的。
                    放行门槛 90%，抽审少于 30 条时准确率没有统计意义。
                </p>
                <div className="rounded-xl border border-slate-200 bg-white p-4">
                    <table className="w-full text-xs">
                        <thead className="text-slate-400">
                            <tr><th className="text-left font-medium pb-1.5">品类</th>
                                <th className="text-right font-medium pb-1.5">已绑定</th>
                                <th className="text-right font-medium pb-1.5">已审</th>
                                <th className="text-right font-medium pb-1.5">判对</th>
                                <th className="text-right font-medium pb-1.5">准确率</th>
                                <th className="text-right font-medium pb-1.5">高风险待看</th>
                                <th className="text-left font-medium pb-1.5 pl-4">可否放行</th></tr>
                        </thead>
                        <tbody>
                            {stats.filter((b) => b.bound > 0).map((b) => {
                                const ok = b.accuracy != null && b.accuracy >= 90 && b.audited >= 30;
                                const why = b.audited === 0 ? '未开始抽审'
                                    : b.audited < 30 ? `抽审 ${b.audited} 条，不足 30`
                                        : (b.accuracy ?? 0) < 90 ? `准确率 ${b.accuracy}% 低于 90%` : '达标';
                                return (
                                    <tr key={b.batch} className="border-t border-slate-100">
                                        <td className="py-1.5 text-slate-700">{b.batch}</td>
                                        <td className="py-1.5 text-right tabular-nums">{b.bound}</td>
                                        <td className="py-1.5 text-right tabular-nums">{b.audited}</td>
                                        <td className="py-1.5 text-right tabular-nums">{b.correct}</td>
                                        <td className="py-1.5 text-right tabular-nums">
                                            {b.accuracy == null ? '—'
                                                : <b className={b.accuracy >= 90 ? 'text-emerald-600' : 'text-amber-600'}>{b.accuracy}%</b>}
                                        </td>
                                        <td className="py-1.5 text-right tabular-nums">{b.highRisk}</td>
                                        <td className={`py-1.5 pl-4 ${ok ? 'text-emerald-600' : 'text-slate-400'}`}>
                                            {b.released ? '已放行' : why}
                                        </td>
                                    </tr>
                                );
                            })}
                        </tbody>
                    </table>
                </div>
            </section>

            <section>
                <h3 className="text-sm font-semibold">五、怎么抽审</h3>
                <p className="text-xs text-slate-500 mt-1 mb-2 leading-relaxed max-w-3xl">
                    命中率高不等于准确率高。抽样在品类内分两层，不随机抽——同样的人工成本下
                    分层抽样的估计精度更高，而且高风险层审出来的错误直接就是改识别的材料。
                </p>
                <div className="rounded-xl border border-amber-200 bg-amber-50/60 p-4 mb-3 text-xs leading-relaxed text-slate-700">
                    <b>这一轮高风险层还要回答一个问题：颜色门该设在哪。</b>
                    候选是按品类圈定的，材质本来就等价，所以七成的行语义分直接打满。
                    总分 = 0.4×颜色 + 0.6×1.0，过门 0.7 就等于
                    <b> ΔE ≤ 37.5</b> —— 判定事实上退化成一条颜色线，而这条线很松：
                    蓝色耳机绑黑色 ABS（ΔE 37.4）也能过。
                    审完之后看被否的那些落在 ΔE 轴的什么位置，拐点就是该用的门。
                    在那之前不拍数字。
                </div>
                <div className="rounded-xl border border-slate-200 bg-white p-4 space-y-3">
                    <table className="w-full text-xs">
                        <thead className="text-slate-400">
                            <tr><th className="text-left font-medium pb-1.5">层</th>
                                <th className="text-left font-medium pb-1.5">判据</th>
                                <th className="text-right font-medium pb-1.5">条数</th>
                                <th className="text-left font-medium pb-1.5 pl-4">抽样</th></tr>
                        </thead>
                        <tbody>
                            <tr className="border-t border-slate-100">
                                <td className="py-1.5">高风险</td><td className="py-1.5">ΔE&gt;20 或总分&lt;0.75</td>
                                <td className="py-1.5 text-right tabular-nums">{high}</td>
                                <td className="py-1.5 pl-4 text-slate-500">抽样率高，错误直接用于改识别</td>
                            </tr>
                            <tr className="border-t border-slate-100">
                                <td className="py-1.5">低风险</td><td className="py-1.5">ΔE≤20 且总分≥0.75</td>
                                <td className="py-1.5 text-right tabular-nums">{bound - high}</td>
                                <td className="py-1.5 pl-4 text-slate-500">抽样率低</td>
                            </tr>
                        </tbody>
                    </table>
                    <p className="text-xs text-slate-500 leading-relaxed">
                        放行判据两条并列：准确率达标 90%；错误不集中——被否的错误若超过一半落在
                        同一模式上，即使准确率达标也不放行，因为系统性错误会被模型学成规律，
                        危害远大于同比例的随机错误。
                    </p>
                </div>
            </section>
        </div>
    );
}

export default function ProductMaterialReview() {
    const initQ = useMemo(() => new URLSearchParams(window.location.search), []);
    const [batch, setBatch] = useState(() => initQ.get('batch') || '');
    // 'all' 是「不筛」的显式值: 空串会被 useSyncQueryToUrl 丢掉, 刷新又回默认档。
    const [risk, setRisk] = useState(() => initQ.get('risk') || 'high');
    const [status, setStatus] = useState(() => initQ.get('status') || 'auto');
    // 'review' 逐条核验 / 'stats' 统计。统计不参与 URL 同步 —— 它没有可分享的筛选状态。
    const [tab, setTab] = useState<'review' | 'stats'>('review');
    const qStatus = status === 'all' ? '' : status;
    // 风险是**绑定结果**的属性 —— ΔE 与总分都来自判定。「没绑上」(review) 与
    // 「等补齐」(blocked) 这两档根本没有判定, 两列恒为 NULL, 而后端的高风险判据是
    // `delta_e > 20 OR total_score < 0.75`:SQL 里 `NULL OR NULL` 求值为 NULL,
    // WHERE 当假 ⇒ 默认的「高风险」档会把这两类**整个滤空且不报错**。
    // 2026-10-12 实测:生产 698 条没绑上, 页面默认档下一条都不显示。
    //
    // 所以这两档一律不带风险筛选, 控件同时禁掉 —— 留一个「看着能点、点了必空」的
    // 筛选比没有更糟, 它会让人以为真的没有数据。
    const riskApplies = qStatus !== 'review' && qStatus !== 'blocked';
    const qRisk = !riskApplies || risk === 'all' ? '' : risk;
    useSyncQueryToUrl('/assets/product-materials', {
        batch,
        risk: risk === 'high' ? '' : risk,
        status: status === 'auto' ? '' : status,
    });

    const [rows, setRows] = useState<ProductMaterialRow[]>([]);
    const [total, setTotal] = useState(0);
    const [page, setPage] = useState(1);
    const [cursor, setCursor] = useState(0);
    const [loading, setLoading] = useState(false);
    const [stats, setStats] = useState<ProductMaterialBatchStat[]>([]);
    const [zoom, setZoom] = useState<string | null>(null);
    const [picked, setPicked] = useState<string>('');   // 当前选中的 xcode; 空=采纳判定结果
    const [remark, setRemark] = useState('');
    const busy = useRef(false);

    const cur = rows[cursor];

    const loadStats = useCallback(() => {
        getProductMaterialStats()
            .then((d) => setStats(d.batches))
            .catch((e) => toast(e instanceof ApiError ? e.message : '统计加载失败'));
    }, []);

    const load = useCallback((p: number, reset: boolean) => {
        setLoading(true);
        listProductMaterials({
            batch: batch || undefined,
            bindStatus: (qStatus || undefined) as BindStatus | undefined,
            risk: (qRisk || undefined) as 'high' | 'low' | undefined,
            page: p, pageSize: PAGE_SIZE,
        })
            .then((d) => {
                setTotal(d.total);
                setPage(p);
                setRows((old) => (reset ? d.list : [...old, ...d.list]));
                if (reset) setCursor(0);
            })
            .catch((e) => toast(e instanceof ApiError ? e.message : '列表加载失败'))
            .finally(() => setLoading(false));
    }, [batch, qStatus, qRisk]);

    useEffect(() => { load(1, true); }, [load]);
    useEffect(() => { loadStats(); }, [loadStats]);
    // 换条目时清掉上一条的选择与备注 —— 留着会把 A 的备注写到 B 上。
    useEffect(() => { setPicked(''); setRemark(''); }, [cursor, cur?.id]);

    // 走到批尾自动续拉, 不等用户点"下一页"。
    const go = useCallback((delta: number) => {
        setCursor((c) => {
            const next = c + delta;
            if (next < 0) return 0;
            if (next >= rows.length) {
                if (rows.length < total && !loading) load(page + 1, false);
                return rows.length ? rows.length - 1 : 0;
            }
            return next;
        });
    }, [rows.length, total, loading, page, load]);

    const submit = useCallback(async (action: 'confirm' | 'reject') => {
        if (!cur || busy.current) return;
        busy.current = true;
        try {
            const xcode = action === 'confirm' && picked ? picked : undefined;
            const d = await verdictProductMaterial(cur.id, { action, xcode, remark });
            // 就地改本行而不是整页重拉: 重拉会把光标弹回批首, 审核节奏全断。
            setRows((old) => old.map((r) => (r.id === cur.id
                ? {
                    ...r, bindStatus: d.bindStatus, remark,
                    switched: !!xcode && xcode !== r.auto?.xcode,
                    chosen: xcode ? (r.alternatives.find((a) => a.xcode === xcode) ?? r.chosen) : r.chosen,
                }
                : r)));
            loadStats();
            go(1);
        } catch (e) {
            toast(e instanceof ApiError ? e.message : '提交失败');
        } finally {
            busy.current = false;
        }
    }, [cur, picked, remark, go, loadStats]);

    // 快捷键。输入框里不拦 —— 否则备注打不了字。
    useEffect(() => {
        if (tab !== 'review') return;
        const onKey = (e: KeyboardEvent) => {
            const t = e.target as HTMLElement;
            if (t && (t.tagName === 'INPUT' || t.tagName === 'TEXTAREA')) return;
            if (e.key === 'ArrowLeft') { e.preventDefault(); go(-1); return; }
            if (e.key === 'ArrowRight') { e.preventDefault(); go(1); return; }
            if (e.key === 'Enter' || e.key.toLowerCase() === 'y') { e.preventDefault(); submit('confirm'); return; }
            if (e.key.toLowerCase() === 'n') { e.preventDefault(); submit('reject'); return; }
            if (/^[1-9]$/.test(e.key)) {
                const i = Number(e.key) - 1;
                const alt = cur?.alternatives[i];
                if (alt) { e.preventDefault(); setPicked(alt.xcode); }
            }
        };
        window.addEventListener('keydown', onKey);
        return () => window.removeEventListener('keydown', onKey);
    }, [go, submit, cur, tab]);

    const batchOptions = useMemo(
        () => [['', `全部品类 (${stats.reduce((s, b) => s + b.total, 0)})`] as [string, string]]
            .concat(stats.map((b) => [b.batch, `${b.batch} (${b.total})`] as [string, string])),
        [stats],
    );
    // **只认显式选中的品类。** 原来 batch 为空 (全部品类) 时回退到 `cur?.batch`,
    // 于是放行栏跟着光标在品类间跳:选择器写着「全部品类」, 栏里却是当前这一行所属的
    // 那个品类, 按 N 翻一行就整个换掉, 点「放行」放的是碰巧停在的那个。
    // 放行是按品类的外发动作 (放行后下游可取), 目标不能由光标位置决定。
    const curStat = batch ? stats.find((b) => b.batch === batch) : undefined;

    // 全部品类档下改摆全局汇总, 让审核时也能看到整体进度。
    // **准确率必须用合计重算, 不能平均各品类的 accuracy** —— 那是按品类等权,
    // 1 条的品类和 1200 条的品类一样重, 算出来的数不对应任何真实比例。
    const allStat = useMemo(() => {
        if (batch || !stats.length) return undefined;
        const s = (f: (b: ProductMaterialBatchStat) => number) =>
            stats.reduce((n, b) => n + f(b), 0);
        const audited = s((b) => b.audited);
        return {
            total: s((b) => b.total), bound: s((b) => b.bound),
            review: s((b) => b.review), blocked: s((b) => b.blocked),
            audited, correct: s((b) => b.correct),
            accuracy: audited ? Math.round((s((b) => b.correct) / audited) * 100) : null,
        };
    }, [batch, stats]);

    // 两种来源共用同一条栏。放行按钮只在 curStat 存在时出 —— 见上。
    const bar = curStat ?? allStat;

    const doRelease = async (released: 0 | 1, force = false) => {
        if (!curStat) return;
        try {
            await releaseProductMaterial({ batch: curStat.batch, released, force });
            toast(released ? `${curStat.batch} 已放行` : `${curStat.batch} 已撤回`);
            loadStats();
        } catch (e) {
            toast(e instanceof ApiError ? e.message : '操作失败');
        }
    };

    return (
        <div className="p-5 space-y-4">
            <div>
                <h1 className="text-lg font-semibold text-slate-800">商品关联官方耗材 · 人工审核</h1>
                <p className="mt-1 text-xs text-slate-500 max-w-3xl leading-relaxed">
                    判断这个官方耗材和图上物体的材质是不是等价、激光参数能不能复用。
                    不是判断「这张图是不是这个耗材的商品图」。
                    {tab === 'review' && <>快捷键：<kbd className="px-1 bg-slate-100 rounded">←</kbd>
                    <kbd className="px-1 bg-slate-100 rounded">→</kbd> 翻条，
                    <kbd className="px-1 bg-slate-100 rounded">Enter</kbd> 确认，
                    <kbd className="px-1 bg-slate-100 rounded">N</kbd> 否决，
                    <kbd className="px-1 bg-slate-100 rounded">1-9</kbd> 换选备选。</>}
                </p>
            </div>

            <div className="flex gap-1 border-b border-slate-200">
                {([['review', '逐条核验'], ['stats', '统计']] as const).map(([v, label]) => (
                    <button
                        key={v}
                        className={`px-3.5 py-1.5 text-[13px] border-b-2 -mb-px transition ${
                            tab === v ? 'border-sky-600 text-sky-700 font-semibold' : 'border-transparent text-slate-400'
                        }`}
                        onClick={() => setTab(v)}
                    >
                        {label}
                    </button>
                ))}
            </div>

            {tab === 'stats' && <StatsView stats={stats} />}

            {tab === 'review' && (
            <>
            <div className="flex flex-wrap items-center gap-3">
                <SearchSelect
                    className="w-52"
                    value={batch}
                    onChange={setBatch}
                    placeholder={batchOptions[0][1]}
                    options={batchOptions.slice(1).map(([value, label]) => ({ value, label }))}
                    clearable
                />
                <SegGroup label="状态" options={STATUS_TABS} value={status} onChange={setStatus} />
                <SegGroup
                    label="风险"
                    options={RISK_TABS}
                    value={riskApplies ? risk : 'all'}
                    onChange={setRisk}
                    disabled={!riskApplies}
                    title={riskApplies ? undefined
                        : '没绑上与等补齐的行没有色差和总分, 风险判据用不上'}
                />
            </div>

            {bar && (
                <div className="flex flex-wrap items-center gap-4 rounded-lg border border-slate-200 bg-slate-50/60 px-4 py-2.5 text-xs">
                    <span className="font-medium text-slate-700">{curStat ? curStat.batch : '全部品类'}</span>
                    <span className="text-slate-500">共 {bar.total}</span>
                    <span className="text-slate-500">已绑定 {bar.bound}</span>
                    <span className="text-slate-500">没绑上 {bar.review}</span>
                    {bar.blocked > 0 && <span className="text-slate-400">等补齐 {bar.blocked}</span>}
                    <span className="text-slate-500">已审 {bar.audited}</span>
                    <span className="text-slate-700">
                        准确率{' '}
                        {bar.accuracy == null
                            ? <span className="text-slate-400">尚无</span>
                            : <b className={bar.accuracy >= 90 ? 'text-emerald-600' : 'text-amber-600'}>
                                {bar.accuracy}%
                            </b>}
                    </span>
                    {curStat && (curStat.released
                        ? (
                            <button
                                className="ml-auto px-2.5 py-1 rounded border border-slate-300 text-slate-600 hover:bg-white"
                                onClick={() => doRelease(0)}
                            >
                                已放行，点此撤回
                            </button>
                        )
                        : (
                            <button
                                className="ml-auto px-2.5 py-1 rounded border border-emerald-300 bg-emerald-50 text-emerald-700 hover:bg-emerald-100"
                                onClick={() => doRelease(1)}
                            >
                                放行这一品类
                            </button>
                        ))}
                </div>
            )}

            {!loading && rows.length === 0 && (
                <NoticeBar>当前筛选没有记录。换个品类或状态试试。</NoticeBar>
            )}

            {cur && (
                <div className="grid gap-4" style={{ gridTemplateColumns: 'minmax(0,1fr) minmax(0,1fr)' }}>
                    {/* 左: 商品侧 */}
                    <div className="rounded-xl border border-slate-200 bg-white p-4">
                        <div className="flex items-center justify-between">
                            <span className="text-xs text-slate-400">
                                第 {cursor + 1} / {total} 条
                            </span>
                            <span className="flex items-center gap-2">
                                {cur.highRisk && (
                                    <span className="px-2 py-0.5 rounded bg-amber-50 text-amber-700 text-[11px]">
                                        高风险
                                    </span>
                                )}
                                <span className={`px-2 py-0.5 rounded text-[11px] ${STATUS_BADGE[cur.bindStatus].cls}`}>
                                    {STATUS_BADGE[cur.bindStatus].text}
                                </span>
                            </span>
                        </div>
                        <ProductImg
                            src={cur.imageUrl}
                            thumb={false}
                            className="mt-3 w-full rounded-lg bg-slate-50 object-contain cursor-zoom-in"
                            style={{ maxHeight: 340 }}
                            onClick={() => cur.imageUrl && setZoom(cur.imageUrl)}
                        />
                        <div className="mt-3 space-y-1.5">
                            <KV label="商品">{cur.title || '—'}</KV>
                            <KV label="人工审定">
                                <b>{cur.auditedL1}/{cur.auditedL2}</b>
                            </KV>
                            <KV label="材料名">
                                {cur.materialNameZh || cur.materialName || '—'}
                                {cur.materialNameZh && cur.materialName && (
                                    <span className="ml-1.5 text-slate-400">{cur.materialName}</span>
                                )}
                            </KV>
                            <KV label="主加工面色"><Swatch hex={cur.colorHex} /></KV>
                            {cur.surfaceProcess && <KV label="表面工艺">{cur.surfaceProcess}</KV>}
                        </div>
                    </div>

                    {/* 右: 耗材侧 */}
                    <div className="rounded-xl border border-slate-200 bg-white p-4 space-y-3">
                        {cur.chosen
                            ? (
                                <div>
                                    <div className="text-xs text-slate-400 mb-1.5">
                                        {picked ? '跑批的判定（未选中）' : '跑批的判定'}
                                    </div>
                                    <CandCard
                                        cand={cur.chosen}
                                        selected={!picked}
                                        onPick={() => setPicked('')}
                                        onZoom={setZoom}
                                    />
                                </div>
                            )
                            : (
                                <NoticeBar>
                                    跑批没绑上。{reasonText(cur.reason)}
                                    {cur.alternatives.length > 0 && '　可以从下面的候选里挑一个确认。'}
                                </NoticeBar>
                            )}

                        {cur.switched && cur.auto && (
                            <div>
                                <div className="text-xs text-slate-400 mb-1.5">
                                    跑批原判（人工已换选，保留它用于算准确率）
                                </div>
                                <CandCard cand={cur.auto} onZoom={setZoom} />
                            </div>
                        )}

                        {cur.alternatives.length > 0 && (
                            <div>
                                <div className="text-xs text-slate-400 mb-1.5">
                                    备选 {cur.alternatives.length} 个（按数字键换选）
                                </div>
                                <div className="space-y-2">
                                    {cur.alternatives.map((a, i) => (
                                        <CandCard
                                            key={a.xcode}
                                            cand={a}
                                            index={i + 1}
                                            selected={picked === a.xcode}
                                            onPick={() => setPicked(a.xcode)}
                                            onZoom={setZoom}
                                        />
                                    ))}
                                </div>
                            </div>
                        )}

                        <input
                            className="w-full text-xs border border-slate-200 rounded px-2.5 py-2"
                            placeholder="有问题就记一句（可空）"
                            value={remark}
                            onChange={(e) => setRemark(e.target.value)}
                        />
                        <div className="flex items-center gap-2 pt-1">
                            <button
                                className="px-3 py-1.5 rounded bg-emerald-600 text-white text-xs hover:bg-emerald-700 disabled:opacity-40"
                                disabled={!cur.chosen && !picked}
                                onClick={() => submit('confirm')}
                            >
                                确认{picked ? '换选的这个' : ''}（Enter）
                            </button>
                            <button
                                className="px-3 py-1.5 rounded border border-rose-300 text-rose-600 text-xs hover:bg-rose-50"
                                onClick={() => submit('reject')}
                            >
                                都不对，否决（N）
                            </button>
                            <span className="ml-auto flex gap-1">
                                <button
                                    className="px-2 py-1.5 rounded border border-slate-200 text-slate-500 text-xs"
                                    onClick={() => go(-1)}
                                >
                                    ←
                                </button>
                                <button
                                    className="px-2 py-1.5 rounded border border-slate-200 text-slate-500 text-xs"
                                    onClick={() => go(1)}
                                >
                                    →
                                </button>
                            </span>
                        </div>
                    </div>
                </div>
            )}

            </>
            )}

            <ImageLightbox src={zoom} onClose={() => setZoom(null)} />
        </div>
    );
}
